import React, { useContext, useEffect, useState, useRef } from "react";
import { WebSocketContext } from "../../context/WebSocketContext";
import { motion } from "framer-motion";
import avatar from "./default-avatar.png";
import Modal from "./LiveViewModal";

const LiveView = () => {
  const { messages } = useContext(WebSocketContext);
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalPosition, setModalPosition] = useState({ left: 0, top: 0 });
  const parentRef = useRef(null);

  const orbits = [
    { radius: 480, minTime: 60 },
    { radius: 380, minTime: 120 },
    { radius: 275, minTime: 180 },
    { radius: 175, minTime: 240 },
    { radius: 100, minTime: 300 },
    { radius: 50, minTime: 360 },
  ];

  const formatTime = (active_time) => {
    if (active_time >= 60) {
      const minutes = Math.floor(active_time / 60);
      const seconds = active_time % 60;
      return `${minutes}m ${seconds}s`;
    }
    return `${active_time}s`;
  };

const getPosition = (id, active_time) => {
  const numericId = Array.from(id).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const angleIncrement = (2 * Math.PI) / 6;
  const fixedAngle = (numericId % 6) * angleIncrement; 

  const time = active_time || 0;

  const orbit = orbits.find((orbit) => time < orbit.minTime) || orbits[5];
  return {
    x: orbit.radius * Math.cos(fixedAngle),
    y: orbit.radius * Math.sin(fixedAngle),
  };
};

  useEffect(() => {
    const latestActiveSessionsMessage = messages.slice().reverse().find((msg) => msg.active_sessions);
  
    if (latestActiveSessionsMessage) {
      setActiveSessions((prevSessions) =>
        latestActiveSessionsMessage.active_sessions.map((newSession) => {
          const existingSession = prevSessions.find((s) => s.id === newSession.id);
          return {
            ...newSession,
            active_time: existingSession ? existingSession.active_time : newSession.active_time,
          };
        })
      );
  
      setSelectedUser((prevSelectedUser) => {
        if (prevSelectedUser && !latestActiveSessionsMessage.active_sessions.some(session => session.id === prevSelectedUser.id)) {
          return null;
        }
        return prevSelectedUser;
      });
    }
  }, [messages]); 
  
  

  useEffect(() => {
    if (activeSessions.length > 0) {
      const id = setInterval(() => {
        setActiveSessions((prevSessions) =>
          prevSessions.map((session) => ({
            ...session,
            active_time: session.active_time + 1, 
          }))
        );
      }, 1000);
  
      return () => clearInterval(id);
    }
  }, [activeSessions]);

  const handleUserClick = (e, session, x, y) => {
    e.stopPropagation();
  if (isNaN(x) || isNaN(y)) {
    console.error("Invalid avatar position:", x, y);
    return;
  }
    setSelectedUser((prev) => (prev?.id === session.id ? prev : session)); // Avoid unnecessary re-renders
  
    const parentRect = parentRef.current.getBoundingClientRect();
    const modalWidth = 384; // Modal width
    const modalHeight = 200; // Approximate modal height
  
    let left = x + parentRect.width / 2;
    let top = y + parentRect.height / 2;
  
    if (left - modalWidth / 2 < 0) {
      left += modalWidth;
    }
  
    const spaceBelow = parentRect.height - (top + modalHeight);
    if (spaceBelow < 0) {
      top -= modalHeight;
    }
  
    setModalPosition({ left, top });
  };
  
  
  useEffect(() => {
    return () => {
      setSelectedUser(null);
    };
  }, []);

  return (
    <div className="p-6 pt-8 rounded-2xl border">
      <p className='text-lg font-semibold text-black mb-8'>Live View</p>
      <div
        ref={parentRef}
        className="relative w-full h-[1040px] mt-7 flex justify-center items-center border rounded-2xl shadow-md overflow-hidden"
        style={{ backgroundColor: "#F9F9F9" }}
        onClick={() => setSelectedUser(null)}
      >
        <div className="absolute w-10 h-10 rounded-full" style={{ backgroundColor: "rgba(66, 151, 63, 1)" }}></div>
        {orbits.map((orbit, index) => (
          <div
            key={index}
            className="absolute border-2 rounded-full"
            style={{
              width: `${orbit.radius * 2}px`,
              height: `${orbit.radius * 2}px`,
              borderColor: "rgba(239, 66, 111, 0.2)",
            }}
          ></div>
        ))}

        {activeSessions.map((session) => {
          const { x, y } = getPosition(session.id, session.active_time);

          return (
            <motion.div
              key={session.id}
              initial={{ x: 0, y: 0, opacity: 0 }}
              animate={{ x, y, opacity: 1 }}
              transition={{ duration: 1 }}
              className="absolute flex flex-row items-center gap-1 ml-20 cursor-pointer"
              onClick={(e) => handleUserClick(e, session, x, y)}
            >
              <div>
                <img
                  src={avatar || "/placeholder.svg"}
                  alt="User Icon"
                  className="w-12 h-12 rounded-full"
                  style={{ border: "4px solid #FFF" }}
                />
              </div>

              {/* Restore the time display */}
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-400 mt-1">{formatTime(session.active_time)}</span>
                <span className="text-xs text-black px-3 -ml-2 py-0.5 rounded-xl " style={{ boxShadow: " 0px 4px 4px 0px #B3B3B340" }}>{session.id}</span>
              </div>
            </motion.div>
          );
        })}

        {selectedUser && (
          <div
            className="absolute z-50 bg-white shadow-lg rounded-xl w-96"
            style={{
              left: modalPosition.left,
              top: modalPosition.top,
              transform: "translate(-50%, -100%)",
              maxWidth: "90%", // Prevents clipping at edges
            }}
          >
            <Modal user={selectedUser} onClose={() => setSelectedUser(null)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveView;